import { useState } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTimeEntryComments } from '@/hooks/timesheets/useTimeEntryComments';
import { TimeEntryCommentHistory } from './TimeEntryCommentHistory';

interface CommentInfoIconProps {
  entryId: string;
  hasComments: boolean;
  className?: string;
}

export function CommentInfoIcon({ entryId, hasComments, className = '' }: CommentInfoIconProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  
  const { data: comments = [], isLoading } = useTimeEntryComments(entryId, isOpen);

  if (!hasComments) return null;

  const iconElement = (
    <Info 
      className={`h-4 w-4 text-blue-500 cursor-pointer hover:text-blue-600 ${className}`}
      aria-label="View comments"
    />
  );

  if (isMobile) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-auto p-1">
            {iconElement}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <TimeEntryCommentHistory 
            entryId={entryId}
            comments={comments}
            isLoading={isLoading}
            isMobile={true}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-auto p-1">
            {iconElement}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm p-0">
          <TimeEntryCommentHistory 
            entryId={entryId}
            comments={comments}
            isLoading={isLoading}
            isMobile={false}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
